<!-- 角色权限展示组件 -->
<template>
  <div class="role-permission">
    <div v-if="!selectedRoleId" class="empty-tip">
      <i class="el-icon-info"></i>
      <p>请先在左侧选择一个角色</p>
    </div>

    <div v-else class="permission-content">
      <!-- 基本信息 -->
      <div class="basic-info-section">
        <div class="section-header">
          <h3>
            <i class="el-icon-user-solid"></i>
            基本信息
          </h3>
          <el-button
            type="primary"
            size="small"
            icon="el-icon-check"
            @click="saveRoleInfo"
            :loading="saving"
          >保存</el-button>
        </div>

        <el-form :model="roleForm" label-width="100px" class="role-form">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="角色名称" required>
                <el-input v-model="roleForm.role_name" placeholder="请输入角色名称"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="状态" required>
                <el-select v-model="roleForm.role_status" placeholder="请选择状态" style="width: 100%">
                  <el-option label="启用" :value="0"></el-option>
                  <el-option label="禁用" :value="1"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </div>

      <!-- 功能权限 -->
      <div class="permission-section-wrapper">
        <div class="section-header">
          <h3>
            <i class="el-icon-setting"></i>
            功能权限
          </h3>
          <div class="header-actions">
            <el-checkbox
              v-model="checkAll"
              @change="handleCheckAllChange"
              :indeterminate="isIndeterminate"
            >全选</el-checkbox>
          </div>
        </div>

        <div class="permission-list-container">
          <div v-for="firstLevel in navData" :key="firstLevel.id" class="permission-row">
            <!-- 左侧：一级菜单 -->
            <div class="first-level-menu">
              <el-checkbox
                :value="isFirstLevelChecked(firstLevel)"
                @change="handleFirstLevelChange(firstLevel, $event)"
              >
                <span class="menu-label">
                  <i class="el-icon-menu"></i>
                  {{ firstLevel.label }}
                </span>
              </el-checkbox>
            </div>

            <!-- 右侧：子菜单横向排列 -->
            <div class="sub-menus">
              <div
                v-for="secondLevel in firstLevel.children"
                :key="secondLevel.id"
                class="sub-menu-item"
              >
                <el-checkbox
                  :value="isSecondLevelChecked(secondLevel)"
                  @change="handleSecondLevelChange(firstLevel, secondLevel, $event)"
                >
                  <span class="sub-menu-label">
                    <!-- <i class="el-icon-document"></i> -->
                    {{ secondLevel.label }}
                  </span>
                </el-checkbox>

                <!-- 三级按钮 -->
                <div
                  v-if="secondLevel.children && secondLevel.children.length"
                  class="third-level-buttons"
                >
                  <el-checkbox
                    v-for="thirdLevel in secondLevel.children"
                    :key="thirdLevel.id"
                    :value="isThirdLevelChecked(thirdLevel)"
                    @change="handleThirdLevelChange(firstLevel, secondLevel, thirdLevel, $event)"
                    class="third-level-checkbox"
                  >{{ thirdLevel.label }}</el-checkbox>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { updateApi } from "@/services/systemPage/admin_role.js";

export default {
  name: "RolePermission",
  props: {
    selectedRoleId: {
      type: Number,
      default: null
    },
    selectedRole: {
      type: Object,
      default: null
    },
    selectedRolePermissions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      navData: [],
      checkedKeys: [],
      roleForm: {
        role_name: "",
        role_status: 0
      },
      saving: false,
      checkAll: false,
      isIndeterminate: false
    };
  },
  watch: {
    selectedRole: {
      handler(newVal) {
        if (newVal) {
          this.roleForm = {
            role_name: newVal.role_name || "",
            role_status:
              newVal.role_status !== undefined ? newVal.role_status : 0
          };
        }
      },
      immediate: true,
      deep: true
    },
    selectedRolePermissions: {
      handler(newVal) {
        // 保持字符串类型的ID数组，因为后端返回的格式如 "0,1,1-1,1-2"
        // navList中的id也是字符串格式，如 "0", "1", "1-1"
        this.checkedKeys = (newVal || []).map(id => String(id));
        // 使用 nextTick 确保数据更新后再检查全选状态
        this.$nextTick(() => {
          this.updateCheckAllStatus();
        });
      },
      immediate: true,
      deep: true
    }
  },
  created() {
    this.navData = this.$store.state.navList;
  },
  methods: {
    /* 更新全选状态 */
    updateCheckAllStatus() {
      const allIds = this.getAllPermissionIds();
      const totalCount = allIds.length;

      if (totalCount === 0) {
        this.checkAll = false;
        this.isIndeterminate = false;
        return;
      }

      // 检查当前navData中的所有ID是否都在checkedKeys中
      const checkedInCurrentNav = allIds.filter(id =>
        this.checkedKeys.includes(id)
      ).length;

      this.checkAll = checkedInCurrentNav === totalCount;
      this.isIndeterminate =
        checkedInCurrentNav > 0 && checkedInCurrentNav < totalCount;
    },

    /* 获取所有权限ID */
    getAllPermissionIds() {
      const ids = [];
      this.navData.forEach(firstLevel => {
        ids.push(firstLevel.id);
        if (firstLevel.children) {
          firstLevel.children.forEach(secondLevel => {
            ids.push(secondLevel.id);
            if (secondLevel.children) {
              secondLevel.children.forEach(thirdLevel => {
                ids.push(thirdLevel.id);
              });
            }
          });
        }
      });
      return ids;
    },

    /* 全选/取消全选 */
    handleCheckAllChange(checked) {
      if (checked) {
        this.checkedKeys = this.getAllPermissionIds();
      } else {
        this.checkedKeys = [];
      }
      this.updateCheckAllStatus();
    },

    /* 判断一级菜单是否选中 */
    isFirstLevelChecked(firstLevel) {
      return this.checkedKeys.includes(firstLevel.id);
    },

    /* 判断二级菜单是否选中 */
    isSecondLevelChecked(secondLevel) {
      return this.checkedKeys.includes(secondLevel.id);
    },

    /* 判断三级按钮是否选中 */
    isThirdLevelChecked(thirdLevel) {
      return this.checkedKeys.includes(thirdLevel.id);
    },

    /* 一级菜单勾选变化 */
    handleFirstLevelChange(firstLevel, checked) {
      if (checked) {
        // 选中：添加自己和所有子孙节点
        if (!this.checkedKeys.includes(firstLevel.id)) {
          this.checkedKeys.push(firstLevel.id);
        }
        if (firstLevel.children) {
          firstLevel.children.forEach(secondLevel => {
            if (!this.checkedKeys.includes(secondLevel.id)) {
              this.checkedKeys.push(secondLevel.id);
            }
            if (secondLevel.children) {
              secondLevel.children.forEach(thirdLevel => {
                if (!this.checkedKeys.includes(thirdLevel.id)) {
                  this.checkedKeys.push(thirdLevel.id);
                }
              });
            }
          });
        }
      } else {
        // 取消：移除自己和所有子孙节点
        this.checkedKeys = this.checkedKeys.filter(id => id !== firstLevel.id);
        if (firstLevel.children) {
          firstLevel.children.forEach(secondLevel => {
            this.checkedKeys = this.checkedKeys.filter(
              id => id !== secondLevel.id
            );
            if (secondLevel.children) {
              secondLevel.children.forEach(thirdLevel => {
                this.checkedKeys = this.checkedKeys.filter(
                  id => id !== thirdLevel.id
                );
              });
            }
          });
        }
      }
      this.updateCheckAllStatus();
    },

    /* 二级菜单勾选变化 */
    handleSecondLevelChange(firstLevel, secondLevel, checked) {
      if (checked) {
        // 选中：添加自己、所有子节点、父节点
        if (!this.checkedKeys.includes(secondLevel.id)) {
          this.checkedKeys.push(secondLevel.id);
        }
        if (!this.checkedKeys.includes(firstLevel.id)) {
          this.checkedKeys.push(firstLevel.id);
        }
        if (secondLevel.children) {
          secondLevel.children.forEach(thirdLevel => {
            if (!this.checkedKeys.includes(thirdLevel.id)) {
              this.checkedKeys.push(thirdLevel.id);
            }
          });
        }
      } else {
        // 取消：移除自己和所有子节点
        this.checkedKeys = this.checkedKeys.filter(id => id !== secondLevel.id);
        if (secondLevel.children) {
          secondLevel.children.forEach(thirdLevel => {
            this.checkedKeys = this.checkedKeys.filter(
              id => id !== thirdLevel.id
            );
          });
        }
        // 检查父节点是否还有其他子节点被选中
        const hasOtherChecked = firstLevel.children.some(
          child =>
            child.id !== secondLevel.id && this.checkedKeys.includes(child.id)
        );
        if (!hasOtherChecked) {
          this.checkedKeys = this.checkedKeys.filter(
            id => id !== firstLevel.id
          );
        }
      }
      this.updateCheckAllStatus();
    },

    /* 三级按钮勾选变化 */
    handleThirdLevelChange(firstLevel, secondLevel, thirdLevel, checked) {
      if (checked) {
        // 选中：添加自己和所有父节点
        if (!this.checkedKeys.includes(thirdLevel.id)) {
          this.checkedKeys.push(thirdLevel.id);
        }
        if (!this.checkedKeys.includes(secondLevel.id)) {
          this.checkedKeys.push(secondLevel.id);
        }
        if (!this.checkedKeys.includes(firstLevel.id)) {
          this.checkedKeys.push(firstLevel.id);
        }
      } else {
        // 取消：移除自己
        this.checkedKeys = this.checkedKeys.filter(id => id !== thirdLevel.id);
        // 检查二级菜单是否还有其他三级被选中
        const hasOtherThirdChecked = secondLevel.children.some(
          child =>
            child.id !== thirdLevel.id && this.checkedKeys.includes(child.id)
        );
        if (!hasOtherThirdChecked) {
          this.checkedKeys = this.checkedKeys.filter(
            id => id !== secondLevel.id
          );
          // 检查一级菜单是否还有其他二级被选中
          const hasOtherSecondChecked = firstLevel.children.some(
            child =>
              child.id !== secondLevel.id && this.checkedKeys.includes(child.id)
          );
          if (!hasOtherSecondChecked) {
            this.checkedKeys = this.checkedKeys.filter(
              id => id !== firstLevel.id
            );
          }
        }
      }
      this.updateCheckAllStatus();
    },

    /* 保存角色信息 */
    async saveRoleInfo() {
      // 验证必填字段
      if (!this.roleForm.role_name) {
        this.$message.error("请输入角色名称");
        return;
      }

      if (this.roleForm.role_status !== 0 && this.roleForm.role_status !== 1) {
        this.$message.error("请选择状态");
        return;
      }

      const role_menu = this.checkedKeys.join(",");

      if (!role_menu) {
        this.$message.error("请至少选择一个权限");
        return;
      }

      this.saving = true;

      try {
        const res = await updateApi({
          id: this.selectedRoleId,
          role_name: this.roleForm.role_name,
          role_status: this.roleForm.role_status,
          role_menu
        });

        if (res.code === 200) {
          this.$message.success("保存成功");

          // 更新后通知父组件，传递最新的角色信息
          this.$emit("role-updated", {
            id: this.selectedRoleId,
            role_name: this.roleForm.role_name,
            role_status: this.roleForm.role_status,
            role_menu
          });

          // 发出全局事件通知角色列表更新
          this.$root.$emit("role-list-updated");
        }
      } catch (error) {
        this.$message.error("保存失败");
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped lang="less">
.role-permission {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 空状态提示 */
.empty-tip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #999;

  i {
    font-size: 64px;
    margin-bottom: 15px;
    color: #ddd;
  }

  p {
    font-size: 16px;
    margin: 0;
  }
}

/* 权限内容 */
.permission-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 基本信息区域 */
.basic-info-section {
  border-bottom: 1px solid #e8e8e8;
  background: #fff;
  padding: 20px;
}

/* 功能权限区域 */
.permission-section-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 20px;
  background: #fafafa;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h3 {
    margin: 0;
    font-size: 16px;
    color: #333;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 10px;

    i {
      color: #f6fff8;
      font-size: 18px;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 15px;

    ::v-deep .el-checkbox {
      font-weight: 500;

      .el-checkbox__label {
        font-size: 14px;
        color: #333;
      }

      .el-checkbox__input.is-checked .el-checkbox__inner {
        background-color: #f6fff8;
        border-color: #f6fff8;
      }

      .el-checkbox__input.is-indeterminate .el-checkbox__inner {
        background-color: #f6fff8;
        border-color: #f6fff8;
      }
    }
  }
}

.role-form {
  margin-top: 15px;

  ::v-deep .el-form-item {
    margin-bottom: 0;
  }
}

/* 权限列表容器 */
.permission-list-container {
  flex: 1;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #dcdcdc;
    border-radius: 4px;

    &:hover {
      background: #c0c0c0;
    }
  }
}

/* 权限行 */
.permission-row {
  display: flex;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.3s;

  &:hover {
    background-color: #fafafa;
  }

  &:last-child {
    border-bottom: none;
  }
}

/* 一级菜单 */
.first-level-menu {
  width: 200px;
  min-width: 200px;
  padding: 20px;
  border-right: 2px solid #e8e8e8;
  // background: linear-gradient(135deg, #f6fff8 0%, #f0f2f5 100%);
  display: flex;
  align-items: flex-start;

  ::v-deep .el-checkbox {
    .el-checkbox__label {
      font-weight: 600;
      font-size: 15px;
      color: #333;

      .menu-label {
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          color: #f6fff8;
          font-size: 18px;
        }
      }
    }

    .el-checkbox__input.is-checked .el-checkbox__inner {
      background-color: #f6fff8;
      border-color: #f6fff8;
    }
  }
}

/* 子菜单区域 */
.sub-menus {
  flex: 1;
  padding: 15px;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: flex-start;
  align-content: flex-start;
}

/* 子菜单项 */
.sub-menu-item {
  min-width: 180px;
  padding: 15px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  transition: all 0.3s;

  &:hover {
    border-color: #f6fff8;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
  }

  ::v-deep .el-checkbox {
    display: block;
    margin-bottom: 10px;

    .el-checkbox__label {
      font-weight: 500;
      font-size: 14px;
      color: #555;

      .sub-menu-label {
        display: flex;
        align-items: center;
        gap: 6px;

        i {
          color: #409eff;
          font-size: 16px;
        }
      }
    }

    .el-checkbox__input.is-checked .el-checkbox__inner {
      background-color: #409eff;
      border-color: #409eff;
    }
  }
}

/* 三级按钮 */
.third-level-buttons {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e8e8e8;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .third-level-checkbox {
    ::v-deep .el-checkbox__label {
      font-size: 13px;
      color: #666;
      font-weight: 400;
    }

    ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner {
      background-color: #67c23a;
      border-color: #67c23a;
    }
  }
}
</style>
